import React from 'react';
import { Button, Container, Grid, Box, Link, Typography, type Theme } from '@mui/material';
import { ProfileAvatar } from '@care/react-component-lib';
import { formatStartDate } from '@/utils/formatStartDate';
import payRangeHelper from '@/utils/payRangeHelper';
import { AmplitudeEvent, trackAmplitudeEvent } from '@/lib/analyticsHelper';
import { getSegmentationJobDetails_getSEOSegmentationJobDetails_seoSegmentationJob_job_hourlyRateRange as hourlyRateRange } from '@/__generated__/getSegmentationJobDetails';
import { JOB_TYPES } from '@/constants';
import { JobType } from '@/__generated__/globalTypes';

interface JobIntroProps {
  title: string;
  hiResImageURL?: string | null;
  city: string;
  state: string;
  joinNowURL: string;
  payRange: hourlyRateRange | null;
  startDate: string;
  type: JobType | null;
}

const JobIntro = ({
  title,
  hiResImageURL,
  city,
  state,
  joinNowURL,
  payRange,
  startDate,
  type,
}: JobIntroProps) => {
  const formattedStartDateAvatar = formatStartDate(startDate);
  const formattedStartDateSubtitle = formatStartDate(startDate, true);
  const payRangeString = payRangeHelper(
    payRange?.hourlyRateTo?.amount || /* istanbul ignore next (default value) */ '',
    payRange?.hourlyRateFrom?.amount || /* istanbul ignore next (default value) */ ''
  );

  const handleJoinToApply = (e: React.MouseEvent<HTMLButtonElement>) => {
    const amplitudeData = {
      cta_text: (e.target as HTMLElement).textContent as string,
      cta_type: 'button',
    };

    trackAmplitudeEvent(AmplitudeEvent.ctaInteracted, amplitudeData);
  };

  return (
    <Container maxWidth="md" disableGutters>
      <Grid container maxWidth={{ md: '780px' }} px={{ md: 3 }}>
        <Grid
          item
          xs={12}
          lg={6}
          sx={(theme) => ({
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: theme.spacing(4),
            [theme.breakpoints.up('md')]: {
              marginTop: theme.spacing(0),
              maxWidth: '315px',
              justifyContent: 'flex-start',
              alignItems: 'start',
            },
          })}>
          <ProfileAvatar
            alt={title}
            size="squareMedium"
            src={hiResImageURL ?? undefined}
            sx={(theme: Theme) => ({
              [theme.breakpoints.down('md')]: { width: '120px', height: '120px', fontSize: '48px' },
              width: '288px',
              height: '288px',
              fontSize: '72px',

              '& .MuiAvatar-root': { width: '100%', height: '100%' },
            })}>
            <Box
              height="100%"
              justifyContent="center"
              display="flex"
              flexDirection="column"
              alignItems="center"
              textAlign="center">
              <Typography
                variant="h2"
                component="div"
                width="100%"
                mb={{ md: 2 }}
                sx={{ fontSize: { md: 'h1.fontSize' } }}>
                {formattedStartDateAvatar.month.toLocaleUpperCase()}
              </Typography>
              <Typography
                variant="h1"
                component="div"
                width="100%"
                m={0}
                sx={{ fontSize: { md: 'display1.fontSize' } }}>
                {formattedStartDateAvatar.day.toUpperCase()}
              </Typography>
            </Box>
          </ProfileAvatar>
        </Grid>
        <Grid item xs={12} lg={5} maxWidth={{ md: '360px' }} justifyContent={{ md: 'flex-start' }}>
          <Box
            sx={(theme) => ({
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              margin: theme.spacing(1),
              width: '100%',

              [theme.breakpoints.up('md')]: {
                margin: theme.spacing(0, 'auto'),
                width: '340px',
                textAlign: 'left',
                alignItems: 'flex-start',
              },
            })}>
            <Typography
              variant="h1"
              sx={(theme) => ({
                marginTop: theme.spacing(1),
                [theme.breakpoints.up('md')]: { marginBottom: theme.spacing(0) },
                [theme.breakpoints.down('md')]: { maxWidth: '90%', textAlign: 'center' },
              })}>
              {title}
            </Typography>
            <Typography variant="body2" mt={1}>
              {`${city}, ${state}`}
            </Typography>
            <Grid container justify-content="center" maxWidth="200px">
              {payRangeString && (
                <Grid
                  item
                  xs={6}
                  md={5}
                  sx={(theme) => ({
                    borderRight: `6px solid ${theme.palette.care?.grey[300]}`,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    marginTop: theme.spacing(1),
                    [theme.breakpoints.up('md')]: { alignItems: 'flex-start' },
                  })}>
                  <Typography variant="h2">{payRangeString}</Typography>
                  <Typography variant="body3" color="secondary">
                    Hourly rate
                  </Typography>
                </Grid>
              )}
              {type && (
                <Grid
                  item
                  xs={6}
                  md={7}
                  sx={(theme) => ({
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    marginTop: theme.spacing(1),
                    borderRight: '0',
                    [theme.breakpoints.up('md')]: { alignItems: 'center' },
                  })}>
                  <>
                    <Typography variant="h2">{JOB_TYPES[type]}</Typography>
                    <Typography variant="body3" color="secondary">
                      Job type
                    </Typography>
                  </>
                </Grid>
              )}
              <Grid
                item
                xs={12}
                mt={1}
                display="flex"
                flexDirection="column"
                alignItems={{ xs: 'center', md: 'flex-start' }}>
                <Typography variant="body2">
                  {`Starts ${formattedStartDateSubtitle.month} ${formattedStartDateSubtitle.day}`}
                </Typography>
              </Grid>
            </Grid>

            <Link href={joinNowURL} rel="nofollow">
              <Button
                color="primary"
                onClick={handleJoinToApply}
                variant="contained"
                sx={(theme) => ({
                  marginTop: theme.spacing(3),
                  [theme.breakpoints.up('md')]: {
                    marginTop: theme.spacing(2),
                    marginBottom: theme.spacing(1),
                  },
                })}>
                Join to apply
              </Button>
            </Link>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default JobIntro;
